/**
 * Dashboard Page
 *
 * Main landing page after login showing quick navigation links.
 * Each section loads its own data when navigated to.
 */

import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  CreditCard,
  Activity,
  UserPlus,
  Settings,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';

import { useAuth, useCan, usePermissionStore } from '@/hooks';
import { Card, CardHeader, CardBody, Badge } from '@/components/common';

// ============================================
// Page Component
// ============================================

export function DashboardPage() {
  const { user } = useAuth();
  // Dynamic RBAC — each quick link appears when the user holds the view permission.
  const canOrgs = useCan('organization.organization.view');
  const canPending = useCan('organization.organization.registration');
  const canUsers = useCan('orgUser.user.view');
  const canPlans = useCan('orgPlan.plan.view');
  const canAudit = useCan('audit.audit.view');
  const canManageRoles = useCan('userMgmt.role.manage');
  const userType = usePermissionStore((s) => s.userType);

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
          Welcome back, {user?.firstName || 'User'}!
        </h1>
        <p className="mt-1 text-foreground-muted dark:text-foreground-dark-muted">
          Select a section from the menu or use the quick links below.
        </p>
      </div>

      {/* Quick Actions Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Organizations */}
        {canOrgs && (
          <QuickActionCard
            to="/organizations"
            icon={<Building2 className="h-6 w-6" />}
            title="Organizations"
            description="View and manage all organizations"
            color="primary"
          />
        )}

        {/* Pending Registrations */}
        {canPending && (
          <QuickActionCard
            to="/pending-registrations"
            icon={<UserPlus className="h-6 w-6" />}
            title="Pending Registrations"
            description="Review pending organization registrations"
            color="warning"
          />
        )}

        {/* Platform Users */}
        {canUsers && (
          <QuickActionCard
            to="/users"
            icon={<Users className="h-6 w-6" />}
            title="Platform Users"
            description="Manage platform users and roles"
            color="success"
          />
        )}

        {/* Subscriptions */}
        {canPlans && (
          <QuickActionCard
            to="/subscriptions"
            icon={<CreditCard className="h-6 w-6" />}
            title="Subscriptions"
            description="View subscription plans"
            color="secondary"
          />
        )}

        {/* Login Audit */}
        {canAudit && (
          <QuickActionCard
            to="/audit"
            icon={<Activity className="h-6 w-6" />}
            title="Login Audit"
            description="View login activity and history"
            color="danger"
          />
        )}

        {/* Settings - available to all authenticated users */}
        {(
          <QuickActionCard
            to="/settings"
            icon={<Settings className="h-6 w-6" />}
            title="Settings"
            description="Configure platform settings"
            color="secondary"
          />
        )}
      </div>

      {/* Role info */}
      <Card>
        <CardHeader
          title="Your Access Level"
          action={
            <div className="flex items-center gap-2">
              {userType && <Badge variant="primary">{userType}</Badge>}
            </div>
          }
        />
        <CardBody>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground dark:text-foreground-dark">
                {user?.orgUserType || 'User'}
              </p>
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                {canManageRoles
                  ? 'Full administrative access with role management'
                  : 'Access is based on the permissions granted to your roles'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ============================================
// Quick Action Card Component
// ============================================

interface QuickActionCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: 'primary' | 'success' | 'warning' | 'danger' | 'secondary';
}

function QuickActionCard({ to, icon, title, description, color }: QuickActionCardProps) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary dark:bg-primary-900/20',
    success: 'bg-success-50 text-success dark:bg-success/20',
    warning: 'bg-warning-50 text-warning dark:bg-warning/20',
    danger: 'bg-danger-50 text-danger dark:bg-danger/20',
    secondary:
      'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
  };

  return (
    <Link
      to={to}
      className="group block rounded-lg border border-border bg-card p-6 transition-all hover:shadow-md hover:border-primary/50 dark:border-border-dark dark:bg-card-dark dark:hover:border-primary/50"
    >
      <div className="flex items-start justify-between">
        <div className={`rounded-full p-3 ${colorClasses[color]}`}>{icon}</div>
        <ArrowRight className="h-5 w-5 text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100 dark:text-foreground-dark-muted" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-foreground dark:text-foreground-dark">
        {title}
      </h3>
      <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
        {description}
      </p>
    </Link>
  );
}

export default DashboardPage;
